import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { BadgePercent, IndianRupee, Loader2, Pencil, Plus, Search, Trash2, Percent, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { RestaurantSelector } from '@/components/RestaurantSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { resturantId: '', categoryId: '', commissionType: 'PERCENTAGE', commissionValue: '', status: true, note: '' };

export const formatCommission = (type, value) =>
  type === 'FLAT' ? `₹${Number(value || 0).toLocaleString('en-IN')} flat` : `${Number(value || 0)}%`;

const CommissionManagement = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const [restaurantFilter, setRestaurantFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Bulk import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { ok, message, summary, errors, dryRun }
  const [downloading, setDownloading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commissions`, {
        params: {
          limit: 200,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(query ? { search: query } : {}),
          ...(restaurantFilter ? { restaurantId: restaurantFilter } : {})
        }
      });
      setRows(res.data?.data || []);
      setCounts(res.data?.counts || { all: 0, active: 0, inactive: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load commission rules');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query, restaurantFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Categories of the restaurant chosen in the form (sub-categories shown
  // as "Parent › Child").
  const loadCategories = useCallback(async (resturantId) => {
    setCategories([]);
    if (!resturantId) return;
    setCategoriesLoading(true);
    try {
      const res = await axios.get(`${API}/categories`, { params: { resturantId, limit: 1000 } });
      const list = (Array.isArray(res.data?.data) ? res.data.data : []).filter((c) => !c.isDeleted);
      const byId = new Map(list.map((c) => [String(c.id), c]));
      setCategories(
        list
          .map((c) => {
            const parentId = c.parentId ? String(c.parentId._id || c.parentId) : null;
            const parent = parentId ? byId.get(parentId) : null;
            return { ...c, label: parent ? `${parent.name} › ${c.name}` : c.name };
          })
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (e) {
      toast.error('Failed to load categories of this restaurant');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Rules already set for the form's restaurant (to grey out those categories).
  const [takenCategoryIds, setTakenCategoryIds] = useState(new Set());
  const loadTaken = useCallback(async (resturantId) => {
    setTakenCategoryIds(new Set());
    if (!resturantId) return;
    try {
      const res = await axios.get(`${API}/commissions`, { params: { restaurantId: resturantId, limit: 200 } });
      setTakenCategoryIds(new Set((res.data?.data || []).map((r) => r.categoryId)));
    } catch { /* non-blocking */ }
  }, []);


  const openCreate = () => {
    setEditing(null);
    const rid = restaurantFilter || '';
    setForm({ ...EMPTY, resturantId: rid });
    setDialogOpen(true);
    loadCategories(rid);
    loadTaken(rid);
  };

  const chooseRestaurant = (rid) => {
    setForm((f) => ({ ...f, resturantId: rid || '', categoryId: '' }));
    loadCategories(rid);
    loadTaken(rid);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      resturantId: r.resturantId || '',
      categoryId: r.categoryId || '',
      commissionType: r.commissionType,
      commissionValue: String(r.commissionValue ?? ''),
      status: r.status,
      note: r.note || ''
    });
    setDialogOpen(true);
    loadCategories(r.resturantId);
    loadTaken(r.resturantId);
  };

  const valueError = (() => {
    if (form.commissionValue === '') return null;
    const v = Number(form.commissionValue);
    if (!Number.isFinite(v) || v < 0) return 'Enter a valid value';
    if (form.commissionType === 'PERCENTAGE' && v > 100) return 'Percentage can be at most 100';
    if (form.commissionType === 'FLAT' && v > 10000) return 'Flat commission can be at most ₹10,000';
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.resturantId) return toast.error('Select a restaurant');
    if (!form.categoryId) return toast.error('Select a category');
    if (form.commissionValue === '' || valueError) return toast.error(valueError || 'Enter the commission value');
    setSaving(true);
    try {
      const payload = { ...form, commissionValue: Number(form.commissionValue) };
      const res = editing
        ? await axios.put(`${API}/commissions/${editing.id}`, payload)
        : await axios.post(`${API}/commissions`, payload);
      toast.success(res.data?.message || 'Saved');
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (r) => {
    setBusyId(r.id);
    const next = !r.status;
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: next } : x)));
    try {
      const res = await axios.patch(`${API}/commissions/${r.id}/status`, { status: next });
      toast.success(res.data?.message || 'Updated');
      setCounts((c) => ({ ...c, active: c.active + (next ? 1 : -1), inactive: c.inactive + (next ? -1 : 1) }));
      if (statusFilter !== 'all') load();
    } catch (err) {
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: !next } : x)));
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the commission rule for "${r.categoryName}" (${r.restaurantName})?\n\nItems in this category will use the restaurant's own commission again.`)) return;
    setBusyId(r.id);
    try {
      await axios.delete(`${API}/commissions/${r.id}`);
      toast.success('Commission rule deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  const downloadSample = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/commissions/template`, {
        params: restaurantFilter ? { restaurantId: restaurantFilter } : {},
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-categories-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error('Failed to download the sample file');
    } finally {
      setDownloading(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!importFile) return toast.error('Choose the filled Excel file first');
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('dryRun', dryRun ? 'true' : 'false');
      const res = await axios.post(`${API}/commissions/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult({ ok: true, ...res.data });
      if (!dryRun) {
        toast.success(res.data?.message || 'Imported');
        load();
      }
    } catch (err) {
      const d = err.response?.data || {};
      setImportResult({ ok: false, message: d.detail || 'Import failed', summary: d.summary, errors: d.errors || [] });
    } finally {
      setImporting(false);
    }
  };

  const openImport = () => {
    setImportFile(null);
    setImportResult(null);
    setImportOpen(true);
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="commission-management-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center">
            <BadgePercent className="text-orange-600" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Commission Management</h1>
            <p className="text-sm text-gray-500">Commission per restaurant category. An item uses its own commission (set in the restaurant's menu), else its category's rule, else the restaurant's own commission.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadSample} disabled={downloading} data-testid="commission-sample">
            {downloading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            Sample file
          </Button>
          <Button variant="outline" onClick={openImport} data-testid="commission-import">
            <Upload size={16} className="mr-2" /> Bulk upload
          </Button>
          <Button onClick={openCreate} style={{ backgroundColor: '#C2410C' }} data-testid="commission-add">
            <Plus size={16} className="mr-2" /> Add Commission
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-lg border bg-gray-50 p-1 w-fit">
          {[
            ['all', 'All', counts.all],
            ['active', 'Active', counts.active],
            ['inactive', 'Inactive', counts.inactive]
          ].map(([v, l, n]) => (
            <button key={v} type="button" onClick={() => setStatusFilter(v)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === v ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
              {l} <span className="text-xs text-gray-400">({n})</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="sm:w-72 [&_label]:hidden [&_button]:mt-0">
            <RestaurantSelector value={restaurantFilter} onChange={(id) => setRestaurantFilter(id || '')} label="" allowClear />
          </div>
          <div className="relative sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9" placeholder="Search category or restaurant" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Table */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Restaurant</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Commission</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-8 rounded bg-gray-100 animate-pulse" /></td></tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center">
                      <BadgePercent className="mx-auto text-gray-300" size={36} />
                      <div className="mt-2 font-medium text-gray-700">{query || statusFilter !== 'all' ? 'No rules match this filter' : 'No commission rules yet'}</div>
                      <div className="text-sm text-gray-500">Add a rule, or download the sample file and bulk upload commissions for all categories.</div>
                    </td>
                  </tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/70">
                    <td className="px-4 py-3 text-gray-700">{r.restaurantName || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {r.parentCategoryName ? <span className="text-gray-400 font-normal">{r.parentCategoryName} › </span> : null}
                        {r.categoryName || 'Deleted category'}
                      </div>
                      {!r.categoryActive && <div className="text-xs text-amber-700">Category is inactive or deleted</div>}
                      {r.note && <div className="text-xs text-gray-500 max-w-[260px] truncate" title={r.note}>{r.note}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 ${r.commissionType === 'FLAT' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                        {r.commissionType === 'FLAT' ? 'Flat' : 'Percentage'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatCommission(r.commissionType, r.commissionValue)}</td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={r.status} disabled={busyId === r.id} onCheckedChange={() => toggleStatus(r)}
                                className="data-[state=checked]:bg-green-600" data-testid={`commission-status-${r.id}`} />
                        <span className={`text-xs font-medium ${r.status ? 'text-green-700' : 'text-gray-500'}`}>{r.status ? 'Active' : 'Inactive'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {r.updatedAt ? format(new Date(r.updatedAt), 'dd MMM yyyy') : '—'}
                      {r.updatedByName && <div>{r.updatedByName}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`commission-edit-${r.id}`}><Pencil size={15} /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={busyId === r.id} onClick={() => remove(r)}><Trash2 size={15} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit commission' : 'Add commission'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <RestaurantSelector value={form.resturantId} onChange={chooseRestaurant} label="1. Restaurant" required />
            </div>

            <div>
              <Label className={!form.resturantId ? 'text-gray-400' : ''}>2. Category <span className="text-red-500">*</span></Label>
              <Select value={form.categoryId} disabled={!form.resturantId || categoriesLoading}
                      onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="commission-category">
                  <SelectValue placeholder={!form.resturantId ? 'Choose a restaurant first' : categoriesLoading ? 'Loading categories…' : 'Select a category'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {categoriesLoading && <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>}
                  {!categoriesLoading && form.resturantId && categories.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">This restaurant has no categories</div>
                  )}
                  {categories.map((c) => {
                    const taken = takenCategoryIds.has(String(c.id)) && String(c.id) !== editing?.categoryId;
                    return (
                      <SelectItem key={c.id} value={String(c.id)} disabled={taken}>
                        {c.label}{c.status === false ? ' (inactive)' : ''}{taken ? ' — has a rule' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <fieldset disabled={!form.categoryId} className={`space-y-5 transition-opacity ${form.categoryId ? '' : 'opacity-50 pointer-events-none'}`}>
            {!form.categoryId && (
              <p className="text-xs text-gray-500 -mb-2">Choose a restaurant and a category to set the commission.</p>
            )}
            <div>
              <Label>3. Commission type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { v: 'PERCENTAGE', label: 'Percentage', icon: Percent, hint: '% of item price' },
                  { v: 'FLAT', label: 'Flat', icon: IndianRupee, hint: '₹ per item' }
                ].map((o) => {
                  const on = form.commissionType === o.v;
                  return (
                    <button type="button" key={o.v} onClick={() => setForm((f) => ({ ...f, commissionType: o.v }))}
                            data-testid={`commission-type-${o.v}`}
                            className={`rounded-lg border-2 p-3 text-left transition-colors ${on ? 'border-orange-500 bg-orange-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <o.icon size={15} className={on ? 'text-orange-600' : 'text-gray-400'} /> {o.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{o.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>4. Commission value <span className="text-red-500">*</span></Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{form.commissionType === 'FLAT' ? '₹' : ''}</span>
                <Input type="number" min="0" step="0.01" max={form.commissionType === 'PERCENTAGE' ? 100 : 10000}
                       className={`${form.commissionType === 'FLAT' ? 'pl-7' : ''} pr-10`}
                       value={form.commissionValue} data-testid="commission-value"
                       onChange={(e) => setForm((f) => ({ ...f, commissionValue: e.target.value }))} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.commissionType === 'PERCENTAGE' ? '%' : ''}</span>
              </div>
              {valueError
                ? <p className="text-xs text-red-600 mt-1">{valueError}</p>
                : <p className="text-xs text-gray-500 mt-1">
                    {form.commissionType === 'PERCENTAGE'
                      ? 'e.g. 15 → ₹100 item sells at ₹115'
                      : 'e.g. 10 → ₹100 item sells at ₹110'}
                  </p>}
            </div>

            <div>
              <Label>Note (optional)</Label>
              <Textarea className="mt-1.5" rows={2} maxLength={300} value={form.note}
                        onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Internal note" />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="font-medium text-sm">Status</div>
                <div className="text-xs text-gray-500">Inactive = items in this category use the restaurant commission.</div>
              </div>
              <Switch checked={form.status} onCheckedChange={(v) => setForm((f) => ({ ...f, status: v }))}
                      className="data-[state=checked]:bg-green-600" />
            </div>
            </fieldset>


            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving} onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !!valueError || !form.resturantId || !form.categoryId || form.commissionValue === ''} style={{ backgroundColor: '#C2410C' }} data-testid="commission-save">
                {saving && <Loader2 size={16} className="mr-2 animate-spin" />}{editing ? 'Save changes' : 'Add commission'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk upload */}
      <Dialog open={importOpen} onOpenChange={(o) => !importing && setImportOpen(o)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk upload commissions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ol className="text-sm text-gray-600 space-y-1 list-decimal pl-5">
              <li>
                Download the sample file — it lists <b>every restaurant category</b>
                {restaurantFilter ? ' of the selected restaurant' : ''} with its current commission.
                <Button type="button" variant="link" className="h-auto p-0 ml-1" onClick={downloadSample} disabled={downloading}>
                  {downloading ? 'Downloading…' : 'Download sample'}
                </Button>
              </li>
              <li>Fill <b>Commission Type</b> (PERCENTAGE / FLAT), <b>Commission Value</b> and <b>Status</b>. Leave both blank to skip a row; write DELETE as the type to remove a rule.</li>
              <li>Upload it here — check it first, then import.</li>
            </ol>

            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-6 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors">
              <FileSpreadsheet className="text-green-600" size={28} />
              <span className="text-sm font-medium">{importFile ? importFile.name : 'Choose the filled .xlsx / .csv file'}</span>
              <span className="text-xs text-gray-500">Max 10 MB</span>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="commission-import-file"
                     onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); e.target.value = ''; }} />
            </label>

            {importResult && (
              <div className={`rounded-lg border p-3 text-sm ${importResult.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                <div className={`flex items-center gap-2 font-medium ${importResult.ok ? 'text-green-800' : 'text-red-800'}`}>
                  {importResult.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                  {importResult.message || importResult.detail}
                </div>
                {importResult.summary && (
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs">
                    {[
                      ['Rows', importResult.summary.totalRows],
                      ['New', importResult.summary.created],
                      ['Updated', importResult.summary.updated],
                      ['Removed', importResult.summary.removed],
                      ['Unchanged', importResult.summary.unchanged],
                      ['Skipped', importResult.summary.skipped]
                    ].map(([l, n]) => (
                      <div key={l} className="rounded bg-white/70 px-2 py-1 text-center">
                        <div className="font-semibold text-gray-900">{n ?? 0}</div>
                        <div className="text-gray-500">{l}</div>
                      </div>
                    ))}
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="mt-3 max-h-48 overflow-auto rounded border bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr><th className="px-2 py-1 text-left w-16">Row</th><th className="px-2 py-1 text-left">Problem</th></tr>
                      </thead>
                      <tbody>
                        {importResult.errors.map((er, idx) => (
                          <tr key={idx} className="border-t"><td className="px-2 py-1">{er.row}</td><td className="px-2 py-1 text-red-700">{er.message}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {importResult.ok && importResult.dryRun && (
                  <div className="mt-2 text-xs text-green-800">No errors found — click <b>Import</b> to save.</div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={importing} onClick={() => setImportOpen(false)}>Close</Button>
              <Button type="button" variant="outline" disabled={importing || !importFile} onClick={() => runImport(true)}>
                {importing ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}Check file
              </Button>
              <Button type="button" disabled={importing || !importFile} onClick={() => runImport(false)}
                      style={{ backgroundColor: '#C2410C' }} data-testid="commission-import-submit">
                {importing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionManagement;
